import React, { useEffect, useState } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { isAuthenticated } from "../utils/auth";
import useZustandStore from "zustandStore";
import {
  isLicenseManagementEnabled,
  isMspLicenseManagementEnabled,
} from "utils/licenseFeature";

import AuthCallback from "../pages/Login/AuthCallback";
import Login from "../pages/Login";
import SignUpProcess from "../pages/Login/MockupPages/SignUpProcess";
import DashboardLayout from "ui/layout";
import Dashboard from "pages/Dashboard";
import AInsight from "pages/AInsight";
import Topology from "pages/Topology";
import Devices from "pages/Devices";
import Events from "pages/Events";
import MspEvents from "pages/MspEvents";
import FirmwareManagements from "pages/FirmwareManagements";
import Clients from "pages/Clients";
import UserManagement from "pages/System/UserManagement";
import RealmSetting from "pages/System/RealmSetting";
import UserProfile from "pages/Profile";
import Realm from "pages/ScopeLevel/Realm";
import Organization from "pages/ScopeLevel/Organization";
import Site from "pages/ScopeLevel/Site";
import UserManagementController from "pages/System/UserManagement/Controller";
import Support from "pages/Support";
import LicenseManagement from "pages/LicenseManagement";
import MspLicenseManagement from "pages/MspLicenseManagement";
import WsDebugger from "pages/WsDebugger";
import Redeem from "pages/Redeem";
import SummaryReport from "pages/SummaryReport";
import SurveillanceDashboard from "pages/Surveillance/Dashboard";
import SurveillanceDevices from "pages/Surveillance/Devices";
import SurveillanceLiveview from "pages/Surveillance/Liveview";
import SurveillancePlayback from "pages/Surveillance/Playback";

import localStorageUtil from "utils/localStorageUtil";
import useDataSourceManager from "hooks/useDataSourceManager";
import { v4 as uuidv4 } from "uuid";

import { Box, Typography, Divider, IconButton } from "@mui/material";
import { ReactComponent as CloseIcon } from "assets/images/icons/btn_close.svg";

import ModalContainer from "components/ModalContainer";
import { SolidButton } from "components/buttons/SolidButton";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;
const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;

const getScopeRules = () => {
  let selectedBreadcrumbs = {
    realm: {},
    orgs: {},
    site: {},
  };

  try {
    selectedBreadcrumbs = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") ||
      JSON.stringify({
        realm: {},
        orgs: {},
        site: {},
      }),
    );
  } catch (error) {
    console.error(
      "[Router] Invalid selectBreadcrumbs:",
      error,
    );
  }

  const realm =
    selectedBreadcrumbs?.realm || {};

  const orgs =
    selectedBreadcrumbs?.orgs || {};

  const site =
    selectedBreadcrumbs?.site || {};

  const isEmptyRealm =
    Object.keys(realm).length === 0;

  const isEmptyOrg =
    Object.keys(orgs).length === 0;

  const isEmptySite =
    Object.keys(site).length === 0;

  return {
    storedSelectedRealmData: realm,
    storedSelectedOrgData: orgs,
    storedSelectedSiteData: site,

    isEmptyRealm,
    isEmptyOrg,
    isEmptySite,

    siteRule:
      !isEmptyRealm &&
      !isEmptyOrg &&
      !isEmptySite,

    standaloneSiteRule:
      realm?.hasManageOrg === false &&
      !isEmptyRealm &&
      isEmptyOrg &&
      !isEmptySite,

    orgRule:
      !isEmptyRealm &&
      !isEmptyOrg &&
      isEmptySite,

    standaloneOrgRule:
      realm?.hasManageOrg === false &&
      !isEmptyRealm &&
      isEmptyOrg &&
      isEmptySite,

    mspRule:
      !isEmptyRealm &&
      isEmptyOrg &&
      isEmptySite,

    realmRule:
      isEmptyRealm &&
      isEmptyOrg &&
      isEmptySite,
  };
};

const getBreadcrumbRules = () => {
  const currentSelectBreadcrumbs = localStorage.getItem("selectBreadcrumbs");
  const storedSelectedRealmData = localStorageUtil.getItemField(
    "selectBreadcrumbs",
    "realm",
  );
  const storedSelectedOrgData = localStorageUtil.getItemField(
    "selectBreadcrumbs",
    "orgs",
  );
  const storedSelectedSiteData = localStorageUtil.getItemField(
    "selectBreadcrumbs",
    "site",
  );

  const isEmptyRealm =
    !storedSelectedRealmData ||
    Object.keys(storedSelectedRealmData).length === 0;
  const isEmptyOrg =
    !storedSelectedOrgData ||
    Object.keys(storedSelectedOrgData).length === 0;
  const isEmptySite =
    !storedSelectedSiteData ||
    Object.keys(storedSelectedSiteData).length === 0;

  return {
    storedSelectedRealmData,
    siteRule: !isEmptyRealm && !isEmptyOrg && !isEmptySite,
    standaloneSiteRule:
      storedSelectedRealmData &&
      storedSelectedRealmData.hasManageOrg === false &&
      !isEmptyRealm &&
      isEmptyOrg &&
      !isEmptySite,
    orgRule: !isEmptyRealm && !isEmptyOrg && isEmptySite,
    standaloneOrgRule:
      storedSelectedRealmData &&
      storedSelectedRealmData.hasManageOrg === false &&
      !isEmptyRealm &&
      isEmptyOrg &&
      isEmptySite,
    mspRule: !isEmptyRealm && isEmptyOrg && isEmptySite,
    realmRule:
      currentSelectBreadcrumbs === null ||
      (isEmptyRealm && isEmptyOrg && isEmptySite),
  };
};

const WhatsNewModal = () => {
  const [newsUuid] = useState(uuidv4());
  const [announcementUuid] = useState(uuidv4());
  const { dataMap, sendRequest } = useDataSourceManager();

  const [openWhatsNewModal, setOpenWhatsNewModal] = useState(false);
  const [modalSteps, setModalSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const getReadIds = (storageKey) => {
    try {
      const value = localStorage.getItem(storageKey);
      if (!value) return [];

      const parsedValue = JSON.parse(value);

      return Array.isArray(parsedValue)
        ? parsedValue.map((id) => String(id))
        : [];
    } catch (error) {
      return [];
    }
  };

  const saveReadIds = (storageKey, ids) => {
    const readIds = getReadIds(storageKey);
    const mergedIds = [
      ...new Set([...readIds, ...ids.map((id) => String(id))]),
    ];

    localStorage.setItem(storageKey, JSON.stringify(mergedIds));
  };
  //轉換格式
  const formatDate = (date) => {
    if (!date) return "";

    const dateValue =
      typeof date === "number" || /^\d+$/.test(String(date))
        ? Number(date) * 1000
        : date;

    return new Date(dateValue).toISOString().split("T")[0];
  };
  useEffect(() => {
    sendRequest("/system/news.read", newsUuid);
    sendRequest("/system/announcement.read", announcementUuid);
  }, [sendRequest, newsUuid, announcementUuid]);

  useEffect(() => {
    const newsResponse = dataMap[newsUuid];
    const announcementResponse = dataMap[announcementUuid];

    if (!newsResponse || !announcementResponse) return;

    const newsData = newsResponse?.result?.data || newsResponse?.data || [];
    const announcementData =
      announcementResponse?.result?.data || announcementResponse?.data || [];

    const readNewsIds = getReadIds("newsIds");
    const readAnnouncementIds = getReadIds("announcementIds");

    const unreadNews = Array.isArray(newsData)
      ? newsData.filter((news) => !readNewsIds.includes(String(news.id)))
      : [];

    const unreadAnnouncements = Array.isArray(announcementData)
      ? announcementData.filter(
        (announcement) =>
          !readAnnouncementIds.includes(String(announcement.id)),
      )
      : [];

    const steps = [];

    if (unreadNews.length > 0) {
      steps.push({
        type: "news",
        title: "What's New",
        storageKey: "newsIds",
        items: unreadNews,
      });
    }

    if (unreadAnnouncements.length > 0) {
      steps.push({
        type: "announcement",
        title: "Announcement",
        storageKey: "announcementIds",
        items: unreadAnnouncements,
      });
    }

    setModalSteps(steps);
    setCurrentStepIndex(0);
    setOpenWhatsNewModal(steps.length > 0);
  }, [dataMap, newsUuid, announcementUuid]);

  const markCurrentStepAsRead = () => {
    const currentStep = modalSteps[currentStepIndex];

    if (!currentStep) return;

    const currentIds = currentStep.items.map((item) => String(item.id));

    saveReadIds(currentStep.storageKey, currentIds);
  };

  const handleNext = () => {
    markCurrentStepAsRead();

    if (currentStepIndex < modalSteps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
      return;
    }

    setOpenWhatsNewModal(false);
  };

  const handleCloseWhatsNewModal = () => {
    modalSteps.forEach((step) => {
      const ids = step.items.map((item) => String(item.id));
      saveReadIds(step.storageKey, ids);
    });

    setOpenWhatsNewModal(false);
  };

  const currentStep = modalSteps[currentStepIndex];
  const isLastStep = currentStepIndex === modalSteps.length - 1;

  return (
    <ModalContainer
      display={openWhatsNewModal}
      modalWidth="600px"
      children={
        <Box
          sx={{
            width: "100%",
            bgcolor: "#FFFFFF",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 600,
                color: "rgba(0, 0, 0, 0.8)",
              }}
            >
              {currentStep?.title}
            </Typography>

            <IconButton size="small" onClick={handleCloseWhatsNewModal}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              px: 2,
              pb: 2,
              maxHeight: "380px",
              overflowY: "auto",
            }}
          >
            {currentStep?.items?.map((item, index) => (
              <Box key={item.id}>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#172664",
                    mb: 1,
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#8A8A8A",
                    mb: 2,
                  }}
                >
                  {formatDate(item.updatedAt || item.createdAt)}
                </Typography>

                <Box
                  sx={{
                    fontSize: "12px",
                    color: "rgba(0, 0, 0, 0.8)",
                    lineHeight: 1.8,
                    mb: 3,

                    "& p": {
                      margin: "0 0 12px 0",
                    },

                    "& h2": {
                      fontSize: "18px",
                      margin: "0 0 12px 0",
                    },

                    "& h3": {
                      fontSize: "16px",
                      margin: "16px 0 8px 0",
                    },

                    "& ol, & ul": {
                      margin: "0 0 16px 0",
                      paddingLeft: "24px",
                    },

                    "& li": {
                      marginBottom: "8px",
                    },

                    "& strong": {
                      fontWeight: 600,
                    },

                    "& em": {
                      fontStyle: "italic",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: item.content || "",
                  }}
                />

                {index !== currentStep.items.length - 1 && (
                  <Divider sx={{ my: 3 }} />
                )}
              </Box>
            ))}
          </Box>

          <Divider />

          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <SolidButton width="72px" height="32px" onClick={handleNext}>
              {isLastStep ? "Close" : "Next"}
            </SolidButton>
          </Box>
        </Box>
      }
    />
  );
};

const NewsModal = ({ element }) => {
  return (
    <>
      <WhatsNewModal />
      {element}
    </>
  );
};

const getNetworkSystemPath = () => {
  try {
    const selectedBreadcrumbs = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    if (selectedBreadcrumbs?.site?.id) return "/site/dashboard";

    const isMspScope =
      selectedBreadcrumbs?.realm?.hasManageOrg === true &&
      !selectedBreadcrumbs?.orgs?.id;

    return isMspScope ? "/msp/organizations" : "/org/sites";
  } catch {
    return "/site/dashboard";
  }
};

const getSelectedSystemDashboardPath = () =>
  localStorage.getItem("selectedSystem") === "surveillance"
    ? "/surveillance/dashboard"
    : "/site/dashboard";

const ProtectedRoute = ({ element }) => {
  const location = useLocation();
  const portalConfig = useZustandStore((state) => state.portalConfig);
  const licenseManagementEnabled = isLicenseManagementEnabled(portalConfig);
  const mspLicenseManagementEnabled = isMspLicenseManagementEnabled(portalConfig);
  const level = location.pathname.split("/")[1];
  const isSurveillanceSystemRoute = level === "surveillance";
  const isSurveillanceSiteScopeRoute =
    isSurveillanceSystemRoute &&
    location.pathname.startsWith("/surveillance/dashboard");
  const effectiveScopeLevel = isSurveillanceSiteScopeRoute ? "site" : level;
  const { authProcess, setAuthProcess } = useAuth();

  if (isSurveillanceSystemRoute && portalConfig?.enableVMS === false) {
    return <Navigate to={getNetworkSystemPath()} replace />;
  }

  if (location.pathname === "/license" && !licenseManagementEnabled) {
    return <Navigate to="/" replace />;
  }

  if (location.pathname === "/msp/license" && !mspLicenseManagementEnabled) {
    return <Navigate to="/" replace />;
  }

  const {
    siteRule,
    standaloneSiteRule,
    orgRule,
    standaloneOrgRule,
    mspRule,
    realmRule,
  } = getBreadcrumbRules();
  let supportSession = null;

  try {
    const storedSupportSession = sessionStorage.getItem("supportSession");

    supportSession = storedSupportSession
      ? JSON.parse(storedSupportSession)
      : null;
  } catch (error) {
    console.error("[ProtectedRoute] Invalid supportSession:", error);

    localStorage.removeItem("supportSession");
  }

  const hasSupportSession = Boolean(
    supportSession?.supportTokenId,
  );

  const isSupportSessionRoute =
    hasSupportSession &&
    (
      level === "site" ||
      (
        level === "org" &&
        location.pathname !== "/org/sites"
      )
    ) &&
    !location.pathname.includes(
      "redeem_delegated_access",
    );

  if (checkMockup === "true") {
    return <NewsModal element={element} />;
  }

  if (isAuthenticated()) {
    // 從 Enter 進 Dashboard 時，不執行 orgRule、mspRule、
    // standaloneOrgRule、realmRule 的 redirect
    if (isSupportSessionRoute) {
      return <NewsModal element={element} />;
    }

    if (!authProcess) {
      setAuthProcess("auth");

      if (siteRule) {
        if (level === "org") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
        }

        if (level === "msp") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (standaloneSiteRule) {
        if (level === "org") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
        }

        if (level === "msp") {
          return <Navigate to="/" />;
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (orgRule) {
        if (effectiveScopeLevel === "site") {
          return <Navigate to="/" />;
        }

        if (level === "msp") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (standaloneOrgRule) {
        if (effectiveScopeLevel === "site" || level === "msp") {
          return <Navigate to="/" />;
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (mspRule) {
        if (effectiveScopeLevel === "site" || level === "org") {
          return <Navigate to="/" />;
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});

          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (realmRule) {
        if (effectiveScopeLevel === "site" || level === "org" || level === "msp") {
          return <Navigate to="/" />;
        }

        return <NewsModal element={element} />;
      }
    }

    return <NewsModal element={element} />;
  }

  if (location.pathname) {
    const fullPath = location.pathname + location.search;

    localStorage.setItem("redirectPath", fullPath);

    const params = new URLSearchParams(location.search || "");

    const vId = params.get("v_id");

    const isInvitationPath = location.pathname.includes("/invitation");

    if (vId && isInvitationPath) {
      sessionStorage.setItem("pendingInvitationId", vId);
    } else {
      sessionStorage.removeItem("pendingInvitationId");
    }

    return <Navigate to="/login" replace />;
  }

  sessionStorage.removeItem("pendingInvitationId");

  return <Navigate to="/login" replace />;
};

const RootRedirect = () => {
  if (!isAuthenticated()) {
    return <Login />;
  }

  let supportSession = null;

  try {
    supportSession = JSON.parse(
      sessionStorage.getItem("supportSession") || "null",
    );
  } catch (error) {
    console.error(
      "[RootRedirect] Invalid supportSession:",
      error,
    );

    sessionStorage.removeItem("supportSession");
  }

  // Support Session 存在時，直接導到對應 Dashboard
  if (supportSession?.supportTokenId) {
    return (
      <Navigate
        to={
          supportSession?.scopeType === "org"
            ? "/org/sites"
            : "/site/dashboard"
        }
        replace
      />
    );
  }

  const {
    realmRule,
    mspRule,
    orgRule,
    isEmptyRealm,
    isEmptySite,
    storedSelectedRealmData,
  } = getScopeRules();

  if (checkEnvironment === "cloud") {
    if (realmRule) {
      return <Navigate to="/realms" replace />;
    }

    if (mspRule) {
      if (
        storedSelectedRealmData?.hasManageOrg === true
      ) {
        return (
          <Navigate
            to="/msp/organizations"
            replace
          />
        );
      }

      return <Navigate to="/org/sites" replace />;
    }

    if (orgRule) {
      return <Navigate to="/org/sites" replace />;
    }

    return <Navigate to={getSelectedSystemDashboardPath()} replace />;
  }

  if (checkEnvironment === "premises") {
    if (!isEmptyRealm && !isEmptySite) {
      return <Navigate to={getSelectedSystemDashboardPath()} replace />;
    }

    return <Navigate to="/org/sites" replace />;
  }

  return <Login />;
};

const routes = [
  {
    path: "/",
    element: <RootRedirect />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/callback",
    element: <AuthCallback />,
  },
  {
    path: "/signup",
    element:
      checkMockup === "true" && checkEnvironment === "cloud" ? (
        <SignUpProcess />
      ) : (
        <Navigate to="*" />
      ),
  },
  {
    path: "/ws-debugger",
    element: <WsDebugger />,
  },
  {
    path: "/",
    element: <DashboardLayout />,
    children: [
      {
        path: "/site/dashboard",
        element: <ProtectedRoute element={<Dashboard />} />,
      },
      {
        path: "/surveillance/dashboard",
        element: <ProtectedRoute element={<SurveillanceDashboard />} />,
      },
      {
        path: "/surveillance/devices",
        element: <ProtectedRoute element={<SurveillanceDevices />} />,
      },
      {
        path: "/surveillance/liveview",
        element: <ProtectedRoute element={<SurveillanceLiveview />} />,
      },
      {
        path: "/surveillance/playback",
        element: <ProtectedRoute element={<SurveillancePlayback />} />,
      },
      {
        path: "/site/AInsight",
        element: <ProtectedRoute element={<AInsight />} />,
      },
      {
        path: "site/topology",
        element: <ProtectedRoute element={<Topology />} />,
      },
      {
        path: "/site/summary-report",
        element: <ProtectedRoute element={<SummaryReport />} />,
      },
      {
        path: "clients",
        element: <ProtectedRoute element={<Clients />} />,
      },
      {
        path: "/account/profile",
        element: <ProtectedRoute element={<UserProfile />} />,
      },
      {
        path: "/realms",
        element: <ProtectedRoute element={<Realm />} />,
      },
      {
        path: "/msp/organizations",
        element: <ProtectedRoute element={<Organization />} />,
      },
      {
        path: "/msp/devices",
        element: <ProtectedRoute element={<Devices />} />,
      },
      {
        path: "/msp/events",
        element: <ProtectedRoute element={<MspEvents />} />,
      },
      {
        path: "/org/sites",
        element: <ProtectedRoute element={<Site />} />,
      },
      {
        path: "invitation",
        element: <ProtectedRoute element={<UserManagementController />} />,
      },
      {
        path: "msp/users",
        element: <ProtectedRoute element={<UserManagement />} />,
      },
      {
        path: "org/users",
        element: <ProtectedRoute element={<UserManagement />} />,
      },
      {
        path: "/org/devices",
        element: <ProtectedRoute element={<Devices />} />,
      },
      {
        path: "/site/devices",
        element: <ProtectedRoute element={<Devices />} />,
      },
      {
        path: "/org/events",
        element: <ProtectedRoute element={<Events />} />,
      },
      {
        path: "/site/events",
        element: <ProtectedRoute element={<Events />} />,
      },
      {
        path: "/org/firmware_managements",
        element: <ProtectedRoute element={<FirmwareManagements />} />,
      },
      {
        path: "/site/firmware_managements",
        element: <ProtectedRoute element={<FirmwareManagements />} />,
      },
      {
        path: "/msp/settings",
        element: <ProtectedRoute element={<RealmSetting />} />,
      },
      {
        path: "/org/settings",
        element: <ProtectedRoute element={<RealmSetting />} />,
      },
      {
        path: "/site/settings",
        element: <ProtectedRoute element={<RealmSetting />} />,
      },
      {
        path: "/msp/support",
        element: <ProtectedRoute element={<Support />} />,
      },
      {
        path: "/msp/license",
        element: <ProtectedRoute element={<MspLicenseManagement />} />,
      },
      {
        path: "/org/support",
        element: <ProtectedRoute element={<Support />} />,
      },
      {
        path: "/site/support",
        element: <ProtectedRoute element={<Support />} />,
      },
      {
        path: "/site/redeem_delegated_access",
        element: <ProtectedRoute element={<Redeem />} />,
      },
      {
        path: "/org/redeem_delegated_access",
        element: <ProtectedRoute element={<Redeem />} />,
      },
      {
        path: "/license",
        element: <ProtectedRoute element={<LicenseManagement />} />,
      },
      {
        path: "*",
        element: <Navigate to="/" />,
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
